import React from 'react';
import { RiDatabaseLine, RiServerLine, RiWifiOffLine } from 'react-icons/ri';

interface EmptyStateProps {
  title: string;
  description: string;
  type?: 'no-data' | 'error' | 'offline';
  /** Custom icon to override the default type-based icon */
  icon?: React.ReactNode;
  /** Alias for actionButton (backward compat) */
  action?: {
    label: string;
    onClick: () => void;
  };
  actionButton?: {
    label: string;
    onClick: () => void;
  };
  /** Compact mode — smaller padding, suitable inside card sections */
  compact?: boolean;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  type = 'no-data',
  icon: customIcon,
  action,
  actionButton,
  compact = false,
}) => {
  // Support both actionButton and action aliases
  const effectiveAction = actionButton || action;

  const getDefaultIcon = () => {
    switch (type) {
      case 'error':
        return <RiServerLine className="w-7 h-7 text-danger-400" />;
      case 'offline':
        return <RiWifiOffLine className="w-7 h-7 text-secondary-400" />;
      default:
        return <RiDatabaseLine className="w-7 h-7 text-secondary-400" />;
    }
  };

  const getIconBg = () => {
    switch (type) {
      case 'error':
        return 'bg-danger-50 ring-1 ring-danger-100';
      case 'offline':
        return 'bg-secondary-100 ring-1 ring-secondary-200';
      default:
        return 'bg-secondary-50 ring-1 ring-secondary-200';
    }
  };

  const getTitleColor = () => {
    switch (type) {
      case 'error':
        return 'text-danger-700';
      case 'offline':
        return 'text-secondary-600';
      default:
        return 'text-secondary-800';
    }
  };

  return (
    <div className={`flex flex-col items-center justify-center ${compact ? 'py-8 px-4' : 'py-16 px-6'}`}>
      <div className="max-w-xs mx-auto text-center">
        {/* Icon container */}
        <div className={`mb-4 inline-flex items-center justify-center ${compact ? 'w-12 h-12 rounded-xl' : 'w-16 h-16 rounded-2xl'} ${getIconBg()}`}>
          {customIcon ? (
            <span className={`${compact ? 'text-secondary-400 [&>svg]:w-5 [&>svg]:h-5' : 'text-secondary-400 [&>svg]:w-7 [&>svg]:h-7'}`}>
              {customIcon}
            </span>
          ) : (
            getDefaultIcon()
          )}
        </div>

        {/* Title */}
        <h2 className={`font-semibold ${getTitleColor()} mb-1.5 ${compact ? 'text-sm' : 'text-base'}`}>
          {title}
        </h2>

        {/* Description */}
        <p className={`text-secondary-600 leading-relaxed ${compact ? 'text-xs mb-4' : 'text-sm mb-6'}`}>
          {description}
        </p>

        {/* Action button */}
        {effectiveAction && (
          <button
            onClick={effectiveAction.onClick}
            className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[38px] py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 shadow-sm hover:shadow-button transition-all duration-200"
          >
            {effectiveAction.label}
          </button>
        )}
      </div>
    </div>
  );
};

export default EmptyState;